import { LibraryModelVersion, Queries } from '~/features/x-ray/library/types';
import { intlService } from '~/i18n/IntlService';
import { sylvanus } from '~/services/serviceInstances';

import {
  LibraryPublishRequest,
  LibraryPublishResponse,
  LibraryPublishStatus,
} from '../../types/sylvanus/library';
import { withQueryFormat } from './searchScopeUtils';

interface RequestParams {
  models: LibraryModelVersion[];
  scopes: Queries;
  isDraft: boolean;
}

type Request = LibraryPublishRequest;

type Response = LibraryPublishResponse;

export const publishLibraryModels = async ({
  models,
  scopes,
  isDraft,
}: RequestParams): Promise<LibraryModelVersion[]> => {
  const request: Request = {
    publishes: models.map((model: LibraryModelVersion) => {
      const query = scopes[model.libraryModelId];

      return {
        library_model_id: model.libraryModelId,
        library_model_version_number: model.version,
        draft: isDraft,
        config_overrides: model.config.scope
          ? {
              scope: {
                data: {
                  attributes: {
                    filters: [],
                    query: withQueryFormat(query),
                    store_as_recent_search: false,
                  },
                },
                type: 'document',
              },
            }
          : null,
      };
    }),
  };
  const response: Response = await sylvanus.post(
    '/library/model/publish',
    request,
  );

  return response.publishes.map((modelPublish) =>
    mapPublishStatusToLibraryModelVersion(
      modelPublish,
      models.find(
        (model) => model.libraryModelId === modelPublish.library_model_id,
      ),
    ),
  );
};

const mapPublishStatusToLibraryModelVersion = (
  modelPublish: LibraryPublishStatus,
  model?: LibraryModelVersion,
): LibraryModelVersion => {
  if (!model) {
    throw new Error(
      intlService.getIntl().formatMessage({
        id: 'publishLibraryModels.modelNotFound',
        defaultMessage: 'Published model not found',
        description: 'Error message when published model is not found',
      }),
    );
  }
  return {
    config: model.config,
    createdDate: model.createdDate,
    description: model.description,
    label: model.label,
    latest: model.latest,
    libraryModelId: modelPublish.library_model_id,
    promptModelId: modelPublish.prompt_model_id ?? null,
    promptModelPublishStatus: model.promptModelPublishStatus,
    publishErrorMessage: modelPublish.error_message ?? undefined,
    publishState: modelPublish.state,
    tags: model.tags,
    targetEntityDetails: model.targetEntityDetails,
    version: modelPublish.library_model_version_number,
  };
};
